// src/features/october/components/ProductFilters.jsx
import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, User, Users, Heart } from 'lucide-react';
import { CATEGORIES_DATA } from '../constants/productConstants';

const formatLabel = (id) => {
  const preset = CATEGORIES_DATA.find((cat) => cat.id === id);
  if (preset) return preset.name;
  return id
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const ProductFilters = ({
  products = [],
  filters = {},
  updateFilter,
  searchQuery = '',
  onSearchChange,
  className = '',
}) => {
  const categories = useMemo(() => {
    const counts = new Map();
    counts.set('all', products.length);

    products.forEach((product) => {
      const tags =
        (Array.isArray(product.categories) && product.categories.length
          ? product.categories
          : [product.category || 'others']).filter(Boolean);

      tags.forEach((tag) => {
        counts.set(tag, (counts.get(tag) || 0) + 1);
      });
    });

    const result = Array.from(counts.entries()).map(([id, count]) => ({
      id,
      name: formatLabel(id),
      count,
    }));

    return result.sort((a, b) => (a.id === 'all' ? -1 : b.id === 'all' ? 1 : a.name.localeCompare(b.name)));
  }, [products]);

  const genderFilters = [
    { id: 'all', name: 'All', icon: Users },
    { id: 'female', name: 'For Her', icon: Heart },
    { id: 'male', name: 'For Him', icon: User },
    { id: 'unisex', name: 'Unisex', icon: Users },
  ];

  const resetFilters = () => {
    updateFilter('category', 'all');
    updateFilter('gender', 'all');
    onSearchChange('');
  };

  const hasActiveFilters =
    (filters.category && filters.category !== 'all') ||
    (filters.gender && filters.gender !== 'all') ||
    searchQuery.trim() !== '';

  const categoryButtonClass = (id, isSelected) =>
    [
      'flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border transition-all duration-200 font-body text-sm',
      isSelected
        ? 'bg-brand-red text-brand-light border-brand-red/60 shadow-[0_20px_50px_-25px_rgba(230,0,0,0.6)]'
        : 'bg-brand-black border-brand-red/25 text-brand-light/70 hover:border-brand-gold/40 hover:text-brand-light',
    ].join(' ');

  const genderButtonClass = (isSelected) =>
    [
      'flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border transition-all duration-200 font-body text-sm',
      isSelected
        ? 'bg-brand-red text-brand-light border-brand-red/60 shadow-[0_20px_50px_-25px_rgba(230,0,0,0.6)]'
        : 'bg-brand-black border-brand-red/25 text-brand-light/70 hover:border-brand-gold/40 hover:text-brand-light',
    ].join(' ');

  return (
    <div className={`space-y-8 ${className}`}>
      <div className="relative">
        <Search
          className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-gold/60"
          size={20}
          strokeWidth={1.5}
        />
        <input
          type="text"
          placeholder="Search by name, notes, or description..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full rounded-2xl border border-brand-red/30 bg-brand-black px-12 py-3 text-brand-light placeholder:text-brand-light/40 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 focus:border-brand-gold/60 transition-all shadow-[0_20px_50px_-30px_rgba(255,195,0,0.35)]"
        />
      </div>

      <div>
        <h3 className="text-xs font-medium uppercase tracking-[0.3em] text-brand-light/50 mb-3">
          Shop For
        </h3>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {genderFilters.map((gender) => {
            const IconComponent = gender.icon;
            const isSelected = filters.gender === gender.id;
            return (
              <motion.button
                key={gender.id}
                onClick={() => updateFilter('gender', gender.id)}
                className={genderButtonClass(isSelected)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <IconComponent className="h-4 w-4" />
                {gender.name}
              </motion.button>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-xs font-medium uppercase tracking-[0.3em] text-brand-light/50 mb-3">
          Categories
        </h3>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => updateFilter('category', category.id)}
              className={categoryButtonClass(category.id, filters.category === category.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="font-medium">{category.name}</span>
              <span
                className={`inline-flex min-w-[2rem] items-center justify-center rounded-full px-2 py-1 text-xs font-medium ${
                  filters.category === category.id
                    ? 'bg-brand-gold text-brand-black'
                    : 'bg-brand-black text-brand-light/60 border border-brand-red/20'
                }`}
              >
                {category.count}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {hasActiveFilters && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-brand-red/20 bg-brand-black/80 px-4 py-4"
          >
            <span className="text-sm font-medium text-brand-light/70">Active filters:</span>

            {searchQuery.trim() && (
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/15 px-3 py-1.5 text-sm text-brand-black">
                Search: “{searchQuery}”
                <button
                  onClick={() => onSearchChange('')}
                  className="text-brand-black/70 hover:text-brand-black"
                  aria-label="Remove search filter"
                >
                  ×
                </button>
              </span>
            )}

            {filters.gender && filters.gender !== 'all' && (
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-red/25 bg-brand-red/15 px-3 py-1.5 text-sm text-brand-light">
                {genderFilters.find((g) => g.id === filters.gender)?.name}
                <button
                  onClick={() => updateFilter('gender', 'all')}
                  className="text-brand-light/80 hover:text-brand-light"
                  aria-label="Remove gender filter"
                >
                  ×
                </button>
              </span>
            )}

            {filters.category && filters.category !== 'all' && (
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-red/25 bg-brand-red/15 px-3 py-1.5 text-sm text-brand-light">
                {formatLabel(filters.category)}
                <button
                  onClick={() => updateFilter('category', 'all')}
                  className="text-brand-light/80 hover:text-brand-light"
                  aria-label="Remove category filter"
                >
                  ×
                </button>
              </span>
            )}

            <button
              onClick={resetFilters}
              className="ml-auto text-sm font-medium text-brand-gold underline-offset-4 hover:text-brand-red"
            >
              Clear all
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="text-sm font-body text-brand-light/60 text-center">
        {hasActiveFilters ? 'Filtered results' : 'All products'} • {products.length} items
      </div>
    </div>
  );
};

export default ProductFilters;